import { LayoutHeader } from "@/Header";
import { createFileRoute } from "@tanstack/react-router";
import {
  useGuardianList,
  GuardianUser,
  useInviteGuardian,
  useUnlinkGuardian,
  useUserInfo,
} from "~/services/customer/myPage";
import { useLoadingStore } from "../../../stores/loading";
import { useEffect, useState } from "react";
import { Dialog } from "@/Dialog";
import axios from "axios";

export const Route = createFileRoute("/customer/layout/user-guardian-link")({
  component: GuardianLink,
});

function GuardianLink() {
  const {
    data: guardianData = { guardians: [], main: false },
    isLoading,
    isFetching,
  } = useGuardianList();
  const guardianList = guardianData.guardians;
  const isMain = guardianData.main;
  const { toggleLoading } = useLoadingStore();
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [invitePhoneNumber, setInvitePhoneNumber] = useState("");
  const [inviteNickname, setInviteNickname] = useState("");
  const inviteMutation = useInviteGuardian();
  const unlinkMutation = useUnlinkGuardian();
  const { data: userInfo } = useUserInfo();
  const [isInviteFeedbackModalVisible, setIsInviteFeedbackModalVisible] =
    useState(false);
  const [inviteFeedbackMessage, setInviteFeedbackMessage] = useState("");
  const [isUnlinkConfirmVisible, setIsUnlinkConfirmVisible] = useState(false);

  const getInviteErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as unknown;
      if (data && typeof data === "object" && "message" in data) {
        const message = (data as { message?: unknown }).message;
        if (typeof message === "string" && message.trim().length > 0) {
          return message;
        }
      }
    }
    return "보호자 초대에 실패했습니다. 다시 시도해주세요.";
  };

  // React Query 로딩 상태와 로딩 스토어 연동
  useEffect(() => {
    toggleLoading(isLoading || isFetching);
  }, [isLoading, isFetching, toggleLoading]);

  // 현재 사용자와 보호자 목록 비교하여 상태 결정
  const getGuardianStatus = (user: GuardianUser): "me" | "other" => {
    // currentUser가 true이면 자기 자신을 의미
    return user.currentUser ? "me" : "other";
  };

  const handleInvite = () => {
    setIsInviteModalVisible(true);
  };

  const handleInviteModalClose = () => {
    setIsInviteModalVisible(false);
    setInvitePhoneNumber("");
    setInviteNickname("");
  };

  const handleInviteFeedbackModalClose = () => {
    setIsInviteFeedbackModalVisible(false);
    setInviteFeedbackMessage("");
  };

  const handleInviteSubmit = () => {
    if (inviteMutation.isPending) return;

    if (!invitePhoneNumber.trim() || !inviteNickname.trim()) {
      setInviteFeedbackMessage("전화번호와 별명을 모두 입력해주세요.");
      setIsInviteFeedbackModalVisible(true);
      return;
    }

    inviteMutation.mutate(
      {
        phoneNumber: invitePhoneNumber.trim(),
        nickname: inviteNickname.trim(),
      },
      {
        onSuccess: (data) => {
          handleInviteModalClose();
          setInviteFeedbackMessage(data?.message ?? "보호자 초대가 완료되었습니다.");
          setIsInviteFeedbackModalVisible(true);
        },
        onError: (error) => {
          console.error("보호자 초대 실패:", error);
          // 실패 시에는 입력 모달을 닫지 않고, 메시지만 모달 내부에 표시
          setInviteFeedbackMessage(getInviteErrorMessage(error));
          setIsInviteFeedbackModalVisible(true);
        },
      },
    );
  };

  // currentUser가 true이고 cdGuardianType이 "SUB"인 항목 찾기
  const currentUserGuardian = guardianList.find(
    (user) => user.currentUser && user.cdGuardianType === "SUB",
  );
  const canUnlink = !!currentUserGuardian;

  const handleUnlink = () => {
    setIsUnlinkConfirmVisible(true);
  };

  const handleUnlinkCancel = () => {
    if (unlinkMutation.isPending) return;
    setIsUnlinkConfirmVisible(false);
  };

  const handleUnlinkConfirm = () => {
    if (unlinkMutation.isPending) return;

    const phoneNumber = userInfo?.phoneNumber?.toString().trim();
    const nickname = currentUserGuardian?.nickname?.trim();

    if (!phoneNumber || !nickname) {
      setIsUnlinkConfirmVisible(false);
      setInviteFeedbackMessage(
        "연동해제에 필요한 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.",
      );
      setIsInviteFeedbackModalVisible(true);
      return;
    }

    unlinkMutation.mutate(
      { phoneNumber, nickname },
      {
        onSuccess: (data) => {
          setIsUnlinkConfirmVisible(false);
          const isOk = data?.statusCode === 200;
          setInviteFeedbackMessage(
            data?.message ??
              (isOk ? "보호자 연동이 해제되었습니다." : "연동해제에 실패했습니다."),
          );
          setIsInviteFeedbackModalVisible(true);
        },
        onError: (error) => {
          setIsUnlinkConfirmVisible(false);
          setInviteFeedbackMessage(getInviteErrorMessage(error));
          setIsInviteFeedbackModalVisible(true);
        },
      },
    );
  };

  return (
    <>
      <LayoutHeader title="보호자 연동" hasBackBtn hasFootPrint={false} />

      <div className="flex flex-col flex-auto bg-white px-4 pt-5">
        {/* 보호자 연동이란? 설명 섹션 */}
        <div className="mb-6">
          <div className="bg-white border-2 border-[#E9E9E9] rounded-xl p-4">
            <div className="mb-2 text-base font-semibold">보호자 연동이란?</div>
            <div className="text-sm text-[#666666] leading-relaxed">
              보호자 정보를 연동하면 등록한 모든 사용자와 함께 반려동물 정보를
              공유하고 멍투데이 서비스를 함께 이용할 수 있습니다. 가족을
              추가하여 더 편리하게 멍투데이 서비스를 이용하세요.
              <br />
              <br />
              연동 요청을 하신 경우 다른 보호자에게 초대할 수 있지만 연동 요청을
              받을 수 없습니다.
              <br />
              <br />
              연동 요청을 받으신 경우 연동 요청을 하실 수 없습니다.
            </div>
          </div>
        </div>

        {/* 보호자 목록 */}
        <div className="flex flex-col gap-3">
          {guardianList.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              보호자 연동 목록이 없습니다.
            </div>
          ) : (
            guardianList.map((user) => {
              const status = getGuardianStatus(user);
              return (
                <div
                  key={user.guardianGroupMappingSeq}
                  className="flex items-center justify-between bg-white border-2 border-[#E9E9E9] rounded-xl p-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 items-center mb-1">
                      <span className="text-base font-semibold">
                        {user.name}
                      </span>
                      {status === "me" && (
                        <span className="text-sm text-gray-400">나</span>
                      )}
                    </div>
                    {status !== "me" && user.nickname && (
                      <div className="mb-2 text-sm text-gray-600">
                        {user.nickname}
                      </div>
                    )}
                    <div className="flex gap-1.5 flex-wrap">
                      {user.pets.map((pet) => (
                        <img
                          key={pet.petSeq}
                          src={pet.fileUrl || ""}
                          alt={pet.petName}
                          className="object-cover w-9 h-9 rounded-full"
                          onError={(e) => {
                            // 이미지 로드 실패 시 기본 이미지로 대체
                            (e.target as HTMLImageElement).src =
                              "https://via.placeholder.com/40";
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* 초대하기 버튼 */}
        {((guardianList.length === 0 && !isMain) || isMain) && (
          <div className="mt-6 mb-4">
            <button
              onClick={handleInvite}
              className="py-3 w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full"
            >
              보호자 연동 초대
            </button>
          </div>
        )}

        {/* 연동해제 하기 버튼 */}
        {canUnlink && (
          <div className="mt-6 mb-4">
            <button
              onClick={handleUnlink}
              className="py-3 w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full"
            >
              연동해제 하기
            </button>
          </div>
        )}
      </div>

      {/* 보호자 연동 요청 모달 */}
      <Dialog
        visible={isInviteModalVisible}
        onClose={handleInviteModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">보호자 연동</div>
          </div>
          <div className="flex flex-col gap-4">
            {/* 보호자 전화번호 */}
            <div className="flex gap-3 items-center">
              <label
                htmlFor="invite-phone"
                className="w-24 text-sm font-medium text-center text-gray-700"
              >
                보호자 전화번호
              </label>
              <input
                id="invite-phone"
                type="tel"
                value={invitePhoneNumber}
                onChange={(e) => setInvitePhoneNumber(e.target.value)}
                placeholder="전화번호를 입력하세요"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            {/* 보호자 별명 */}
            <div className="flex gap-3 items-center">
              <label
                htmlFor="invite-nickname"
                className="w-24 text-sm font-medium text-center text-gray-700"
              >
                보호자 별명
              </label>
              <input
                id="invite-nickname"
                type="text"
                value={inviteNickname}
                onChange={(e) => setInviteNickname(e.target.value)}
                placeholder="별명을 입력하세요"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>
          {/* 버튼 */}
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
              onClick={handleInviteModalClose}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full transition-colors hover:bg-orange-500 active:bg-orange-600"
              onClick={handleInviteSubmit}
            >
              {inviteMutation.isPending ? "초대 중..." : "초대"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* 초대 결과/에러 안내 모달 */}
      <Dialog
        visible={isInviteFeedbackModalVisible}
        onClose={handleInviteFeedbackModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold text-center">안내</div>
          <div className="text-sm leading-relaxed text-center text-gray-700 whitespace-pre-line">
            {inviteFeedbackMessage}
          </div>
          <button
            className="py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={handleInviteFeedbackModalClose}
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 보호자 연동 해제 확인 모달 */}
      <Dialog
        visible={isUnlinkConfirmVisible}
        onClose={handleUnlinkCancel}
        persist={false}
      >
        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold text-center">보호자 연동 해제</div>
          <div className="text-sm leading-relaxed text-center text-gray-700">
            보호자 연동을 해제하시겠습니까?
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full disabled:opacity-50"
              onClick={handleUnlinkCancel}
              disabled={unlinkMutation.isPending}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full transition-colors hover:bg-orange-500 active:bg-orange-600 disabled:opacity-50"
              onClick={handleUnlinkConfirm}
              disabled={unlinkMutation.isPending}
            >
              {unlinkMutation.isPending ? "해제 중..." : "해제"}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
